import { ANGLE_RATIO, HEX_DIRECTIONS, HEX_SIZE, TERRAINS } from '@/core/constants/battle.constants';
import type { Hex } from '@/core/types/battle.types';


export function axialToPixel(q: number, r: number) {
  // HEX_SIZE — це відстань від центру до вершини
  const x = HEX_SIZE * (3/2 * q);
  const y = (HEX_SIZE * (Math.sqrt(3) * (r + q / 2))) * ANGLE_RATIO;
  
  return { x, y };
}

export function pixelToAxial(x: number, y: number): { q: number, r: number } {
    const q = (2/3 * x) / HEX_SIZE;
    const r = y / (ANGLE_RATIO * HEX_SIZE * Math.sqrt(3)) - q / 2;
    return hexRound(q, r);
}

/**
 * Convert hex ID "q:r" to coordinates {q, r}
 */
export function idToCoords(id: string): { q: number; r: number } {
    const [q, r] = id.split(':').map(Number);
    return { q: q ?? 0, r: r ?? 0 };
}

/**
 * Convert coordinates {q, r} to hex ID "q:r"
 */
export function coordsToId({ q, r }: { q: number; r: number }): string {
    return q + ":" + r;
}

/**
 * Convert Axial to Cube coordinates
 */
export function axialToCube(q: number, r: number) {
    return {
        x: q,
        y: r,
        z: -q - r
    };
}

/**
 * Calculate hex distance between two points
 */
export function getHexDistance(
    a: { q: number; r: number },
    b: { q: number; r: number }
): number {
    return Math.max(
        Math.abs(a.q - b.q),
        Math.abs(a.r - b.r),
        Math.abs((-a.q - a.r) - (-b.q - b.r))
    );
}

export function getLerpPoint(
    a: { q: number; r: number },
    b: { q: number; r: number },
    t: number // від 0.0 до 1.0 (наприклад, hexesPerRound / totalDistance)
): { q: number, r: number } {
    // 1. Лінійна інтерполяція (дробова)
    const fq = a.q + (b.q - a.q) * t;
    const fr = a.r + (b.r - a.r) * t;
    const fs = -fq - fr;

    // 2. Округлення до Cube Coordinates
    let q = Math.round(fq);
    let r = Math.round(fr);
    let s = Math.round(fs);

    const qDiff = Math.abs(q - fq);
    const rDiff = Math.abs(r - fr);
    const sDiff = Math.abs(s - fs);

    // 3. Корекція похибки (сума має бути 0)
    if (qDiff > rDiff && qDiff > sDiff) {
        q = -r - s;
    } else if (rDiff > sDiff) {
        r = -q - s;
    }

    return { q, r };
}

export function hexRound(floatQ: number, floatR: number) {
    let q = Math.round(floatQ);
    let r = Math.round(floatR);
    let s = Math.round(-floatQ - floatR);

    const qDiff = Math.abs(q - floatQ);
    const rDiff = Math.abs(r - floatR);
    const sDiff = Math.abs(s - (-floatQ - floatR));

    if (qDiff > rDiff && qDiff > sDiff) {
        q = -r - s;
    } else if (rDiff > sDiff) {
        r = -q - s;
    }
    // s нам не потрібен для Axial, повертаємо q та r
    return { q, r };
}

/**
 * Get axial neighbors
 */
export function getAxialNeighbors(q: number, r: number) {
    return HEX_DIRECTIONS.map(d => ({
        q: q + d.q,
        r: r + d.r
    }));
}

/**
 * Get hex line between two points
 */
export function getHexLine(
    a: { q: number; r: number },
    b: { q: number; r: number }
): string[] {
    const dist = getHexDistance(a, b);
    const results: string[] = [];
    
    for (let i = 0; i <= dist; i++) {
        const t = dist === 0 ? 0 : i / dist;
        const q = Math.round(a.q + (b.q - a.q) * t);
        const r = Math.round(a.r + (b.r - a.r) * t);
        results.push(`${q}:${r}`);
    }
    
    return results;
}

/**
 * Перевіряє, чи є пряма видимість між двома гексами.
 * Використовує дробову інтерполяцію для уникнення помилок округлення на межах.
 */
export function hasLineOfSight(
    start: { q: number; r: number },
    end: { q: number; r: number },
    battleMap: Map<string, Hex>
): boolean {
    const dist = getHexDistance(start, end);
    if (dist <= 1) return true; // Сусідів завжди видно

    // Крок для інтерполяції
    for (let i = 1; i < dist; i++) {
        const t = i / dist;
        
        // Використовуємо твій алгоритм округлення, 
        // але з невеликим мікро-зміщенням (epsilon), щоб лінія не "застрягала" на ребрах
        const epsilon = 1e-6;
        const fq = start.q + (end.q - start.q) * t + epsilon;
        const fr = start.r + (end.r - start.r) * t + epsilon;
        
        const { q, r } = hexRound(fq, fr);
        const hexId = coordsToId({ q, r });
        const hex = battleMap.get(hexId);

        // Якщо на шляху є перешкода (obstacle або інша сутність, якщо треба)
        if (!hex || hex.isObstacle) {
            return false;
        }
    }

    return true;
}

/**
 * Швидка перевірка видимості для AI.
 * Пропускає початкову та кінцеву точки.
 */
export function isLineBlocked(
    start: { q: number, r: number },
    target: { q: number, r: number },
    battleMap: Map<string, Hex>
): boolean {
    const line = getHexLine(start, target);
    if (line.length <= 2) return false;

    for (let i = 1; i < line.length - 1; i++) {
        const hexId = line[i]!; 
        const hex = battleMap.get(hexId);
        
        if (hex?.isObstacle || hex?.entityId) {
            return true; 
        }
    }
    return false;
}



export function getMapRows(size: number) {
    return Math.floor(size - (size * 0.24))
}

export function getMapCenter(size: number) {
    const q = Math.floor(size / 2);
    // rOffset — це те саме зміщення, яке ти використовуєш у циклах
    const rOffset = Math.floor(q / 2);
    const rows = getMapRows(size);
    const r = Math.floor(rows / 2) - rOffset;

    return { q, r };
}

export function getSpawnPoints(size: number, mid: { q: number, r: number }) {
    const qOffset = Math.floor(size / 4);
    
    // --- ГРАВЕЦЬ (Ліва сторона) ---
    const p1Front = {
        q: mid.q - qOffset,
        r: mid.r + Math.floor(qOffset / 2)
    };
    const p1Back = {
        q: p1Front.q - 1, // Тил на 1 гекс лівіше
        r: p1Front.r
    };

    // --- ВОРОГ (Права сторона) ---
    const p2Front = {
        q: mid.q + qOffset,
        r: mid.r - Math.floor(qOffset / 2)
    };
    const p2Back = {
        q: p2Front.q + 1, // Тил на 1 гекс правіше
        r: p2Front.r
    };

    return { 
        player: { front: p1Front, back: p1Back }, 
        enemy: { front: p2Front, back: p2Back } 
    };
}

/**
 * Offset neighbors (legacy, for backward compatibility)
 */
export function getOffsetNeighbors(q: number, r: number) {
    const isEven = q % 2 === 0;
    return isEven 
        ? [
            {q: q+1, r: r-1}, {q: q+1, r: r}, {q: q, r: r+1},
            {q: q-1, r: r}, {q: q-1, r: r-1}, {q: q, r: r-1}
          ]
        : [
            {q: q+1, r: r}, {q: q+1, r: r+1}, {q: q, r: r+1},
            {q: q-1, r: r+1}, {q: q-1, r: r}, {q: q, r: r-1}
          ];
}



